import type { FC } from 'react'
import { ChevronRight } from 'lucide-react'
import type { TimelineEvent } from '../core/dashboard-types'
import CardInfoButton from './CardInfoButton'

const timelineMeta = {
  description: 'Weekly planner showing scheduled PMS jobs, surveys, and inspections across vessels.',
  endpoint: 'GET /pms/calendar-events?from={now}&to={now+14d}&companyId={cgaId}',
  logic: 'Fetches calendar events across a 14-day window. Positions events as proportional bars on a weekly Gantt-like timeline, coloured by event type. Only the next 7 days are shown on the card.',
}

type Props = {
  events: TimelineEvent[]
  onViewAll?: () => void
}

const dayLabels = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun']

const barColors: Record<TimelineEvent['color'], { bg: string; border: string; dot: string; text: string }> = {
  red: { bg: 'bg-red-100', border: 'border-red-200', dot: 'bg-red-500', text: 'text-red-700' },
  emerald: { bg: 'bg-emerald-100', border: 'border-emerald-200', dot: 'bg-emerald-500', text: 'text-emerald-700' },
  purple: { bg: 'bg-purple-100', border: 'border-purple-200', dot: 'bg-purple-500', text: 'text-purple-700' },
  orange: { bg: 'bg-orange-100', border: 'border-orange-200', dot: 'bg-orange-500', text: 'text-orange-700' },
  blue: { bg: 'bg-blue-100', border: 'border-blue-200', dot: 'bg-blue-500', text: 'text-blue-700' },
  teal: { bg: 'bg-teal-100', border: 'border-teal-200', dot: 'bg-teal-500', text: 'text-teal-700' },
}

const colorToLabel: Record<TimelineEvent['color'], string> = {
  red: 'Maintenance',
  emerald: 'Crew',
  purple: 'Port Call',
  orange: 'Dry Dock',
  blue: 'Inspection',
  teal: 'Certificates',
}

const SchedulerTimeline: FC<Props> = ({ events, onViewAll }) => {
  if (!events.length) return null

  // Build day headers starting from today
  const today = new Date()
  const dayHeaders = Array.from({ length: 7 }, (_, i) => {
    const d = new Date(today)
    d.setDate(d.getDate() + i)
    return {
      date: d.getDate(),
      month: d.toLocaleString('en-US', { month: 'short' }),
      day: dayLabels[d.getDay()],
      isToday: i === 0,
    }
  })

  return (
    <div className='card-hover bg-white rounded-2xl p-5 border border-slate-200 shadow-sm flex-1'>
      <div className='flex items-center justify-between mb-4'>
        <div className='flex items-center gap-2'>
          <h3 className='text-sm font-bold text-slate-900'>Scheduler / Timeline</h3>
          <CardInfoButton meta={timelineMeta} />
          <span className='text-[11px] text-slate-400'>Next 7 Days</span>
        </div>
        <button className='text-[11px] text-blue-600 hover:underline flex items-center gap-0.5' onClick={onViewAll}>
          View full calendar <ChevronRight className='w-3 h-3' />
        </button>
      </div>

      {/* Day headers */}
      <div className='flex items-center gap-1 mb-4 text-center overflow-x-auto scrollbar-hide'>
        {dayHeaders.map((h, i) => (
          <div
            key={i}
            className={`flex-1 min-w-[48px] py-1.5 rounded-lg ${h.isToday ? 'bg-blue-600 text-white' : ''}`}
          >
            <div className={`text-[10px] ${h.isToday ? 'text-blue-100' : 'text-slate-400'}`}>
              {h.date} {h.month}
            </div>
            <div className='text-[11px] font-medium'>{h.day}</div>
          </div>
        ))}
      </div>

      {/* Timeline bars */}
      <div className='space-y-2.5'>
        {events.map((event) => {
          const colors = barColors[event.color]
          return (
            <div key={event.id} className='flex items-center gap-2'>
              <span className='text-[11px] text-slate-600 w-[76px] flex-shrink-0 truncate' title={event.vessel}>
                {event.vessel}
              </span>
              <div className='flex-1 h-5 relative'>
                <div
                  className={`h-full ${colors.bg} border ${colors.border} rounded absolute flex items-center px-1.5 gap-1 overflow-hidden`}
                  style={{ left: `${event.leftPct}%`, width: `${event.widthPct}%` }}
                >
                  <span className={`w-1.5 h-1.5 rounded-full ${colors.dot} flex-shrink-0`} />
                  <span className={`text-[10px] ${colors.text} font-medium whitespace-nowrap`}>{event.label}</span>
                </div>
              </div>
            </div>
          )
        })}
      </div>

      {/* Legend */}
      <div className='flex items-center gap-4 mt-3 pt-3 border-t border-slate-100 flex-wrap'>
        {(['red', 'emerald', 'purple', 'orange', 'blue', 'teal'] as const).map((c) => {
          const found = events.find((e) => e.color === c)
          if (!found) return null
          return (
            <div key={c} className='flex items-center gap-1.5 text-[10px] text-slate-500'>
              <span className={`w-2 h-2 rounded-full ${barColors[c].dot}`}></span>
              {colorToLabel[c]}
            </div>
          )
        })}
      </div>
    </div>
  )
}

export default SchedulerTimeline
